/**
 * Eden Treaty client (ADR #13). Imports the Elysia `App` *type* directly, so
 * every call is checked against the real server routes at compile time — no
 * OpenAPI codegen, no drift. The type import is erased at build; no server code
 * ships to the browser.
 */
import { treaty } from "@elysiajs/eden";
import type { App } from "../server/app.ts";
import { analyticsOptedOut, NO_ANALYTICS_HEADER } from "./lib/analytics.ts";

// Same-origin: the SPA and API are served by one process.
export const api = treaty<App>(
  typeof window !== "undefined" ? window.location.origin : "http://localhost:3000",
  {
    // A browser that turned usage analytics off in Settings asks the server
    // not to record its changes either (server/analytics.ts).
    headers: () => (analyticsOptedOut() ? { [NO_ANALYTICS_HEADER]: "1" } : undefined),
    // Eden's parser turns any string shaped like a date into a `Date`,
    // whatever the route's type says: a note of "2026-09-14" would reach React
    // as an object. Every string stays a string; `Wire` says so to the types.
    parseDate: false,
  },
).api;

/**
 * A response as the client receives it: each `t.Date()` field, which Eden
 * types as `Date`, arrives as its ISO string. The hooks in lib/queries.ts
 * turn the ones they read back into `Date`s.
 */
export type Wire<T> = T extends Date
  ? string
  : T extends readonly (infer U)[]
    ? Wire<U>[]
    : T extends object
      ? { [K in keyof T]: Wire<T[K]> }
      : T;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function cleanText(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (value instanceof Error) return cleanText(value.message);
  if (!isRecord(value)) return undefined;
  return cleanText(value.message) ?? cleanText(value.error);
}

function edenValue(error: unknown): unknown {
  return isRecord(error) && "value" in error ? error.value : error;
}

export function apiErrorMessage(error: unknown): string {
  const value = edenValue(error);
  const payload = isRecord(value) ? value : undefined;
  const primary =
    cleanText(payload?.error) ??
    cleanText(payload?.message) ??
    cleanText(value) ??
    (error instanceof Error && error.message !== "[object Object]"
      ? cleanText(error.message)
      : undefined) ??
    "Request failed";
  const detail = cleanText(payload?.detail);
  return detail && detail !== primary ? `${primary}: ${detail}` : primary;
}

export class ApiError extends Error {
  readonly status?: number;
  readonly value: unknown;

  constructor(error: unknown) {
    super(apiErrorMessage(error), { cause: error });
    this.name = "ApiError";
    this.status = isRecord(error) && typeof error.status === "number" ? error.status : undefined;
    this.value = edenValue(error);
  }
}

/** Unwrap an Eden response, throwing on error so TanStack Query sees a rejection. */
export async function unwrap<T>(
  promise: Promise<{ data: T | null; error: unknown }>,
): Promise<Wire<T>> {
  const { data, error } = await promise;
  if (error) throw new ApiError(error);
  return data as Wire<T>;
}
